import Page from '../../../components/PageWrapper';
import { useMemo, useState } from 'react';
import GTable from '../../../components/elements/GTable';
import useModal from '../../../hooks/modalHook';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LiaReadme } from 'react-icons/lia';
import { GTableActionColumn, ITablePaginationData, ITableRowAction } from '../../../components/elements/GTable/GTable';
import useRoute from '../../../hooks/routesHook';
import useUserAccount from '../../../hooks/userAccountHook';
import useLayoutHook from '../../../hooks/layoutHook';
import Button from '../../../components/form-control/Button';
import { deleteBlogPostById, getAllBlogPosts, publishBlogPostById, setPostOfTheDay } from '../../../api/requests';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Blog } from '../../../models/blog/Blog';
import ActionConfirmation from '../../../components/elements/ActionConfirmation';
import IconStatus from '../../../components/elements/IconStatus';
import { Eye, EyeOff, Pencil, Star, Trash2 } from 'lucide-react';
import { showToast } from '../../../utils/miscFunctions';

const Blogs = (): JSX.Element => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchString, setSearchString] = useState<string>('');
  const [showPerPage, setShowPerPage] = useState<number>(50);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [blogsPaginationData, setBlogsPaginationData] = useState<ITablePaginationData>({
    total: 0,
    per_page: 0,
    totalPages: 0,
    initialPage: currentPage ? currentPage : 0,
    to: 0,
    from: 0
  });
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { getDashboardBase } = useRoute();
  const { screenWidth } = useLayoutHook();
  const { user } = useUserAccount();
  const queryClient = useQueryClient();

  const getBlogs = () => {
    getAllBlogPosts(currentPage ? currentPage : 0, false, showPerPage)
      .then((response: any) => {

        setBlogs(response?.content);

        const from = response?.pageable?.offset + 1;
        const to =
          from + response?.size > response?.totalElements
            ? response?.totalElements
            : from + response?.size - 1;
        setBlogsPaginationData({
          per_page: response?.size,
          total: response?.totalElements,
          totalPages: response?.totalPages,
          from: from,
          to: to
        });
      })
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const { } = useQuery({
    queryKey: ['getBlogs', showPerPage, currentPage],
    queryFn: () => getBlogs()
  });

  const getTableRowActions = (data: Blog): ITableRowAction[] => {
    const actions: ITableRowAction[] = [];

    if (user.isOwner()) {
      actions.push({
        label: data?.publish ? 'Unpublish' : 'Publish',
        icon: data?.publish ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />,
        callback: (blog: Blog) => {
          blog?.publish ? handleUnpublishBlogPost(blog) : handlePublishBlogPost(blog);
        },
        data: data
      });
    }
    if (user.isOwner() && data.publish) {
      actions.push({
        label: 'Set to post of the day',
        icon: <Star className="h-4 w-4" />,
        callback: (blog: Blog) => handleBlogSetPostOfTheDay(blog),
        data: data
      });
    }

    actions.push(
      {
        label: 'Update',
        icon: <Pencil className="h-4 w-4" />,
        callback: (blog: Blog) =>
          navigate(`${getDashboardBase()}/blogs/${blog?.id}/edit`, { replace: true }),
        data: data
      },
      {
        label: 'Delete',
        icon: <Trash2 className="h-4 w-4" />,
        callback: (blog: Blog) => handleDeleteBlogPost(blog),
        data: data
      },
      {
        label: 'Preview',
        icon: <Eye className="h-4 w-4" />,
        callback: (blog: Blog) => handleBlogPreview(blog),
        data: data
      }
    );

    return actions;
  };


  const handleDeleteBlogPost = (data: Blog) => {
    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="delete"
          modalName="Blog"
          type='danger'
          resourceType={data.title}
          resourceName={'blog post'}
          onClose={closeModal}
          onConfirm={() => {
            deleteBlogPostById(data.id)
              .then((response: any) => {
                if (response) {
                  closeModal();
                  showToast.success(response?.message ?? 'Post deleted successfully', );
                  queryClient.invalidateQueries({
                    queryKey: ['getBlogs']
                  });
                }
              })
              .catch((error: any) => {
                closeModal();
                showToast.error(error?.description ?? 'Could not delete the post. Please try again.', );
              });
          }}
        />
      )
    });
  };

  const handleBlogPreview = (blog: Blog) => {
    window.open(`${process.env.REACT_APP_CLIENT_WEBSITE}/car-stories/${blog?.id}`, '_blank');
  };


  const handlePublishBlogPost = async (blog: Blog) => {
    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="publish"
          modalName="Blog"
          type='success'
          resourceType={blog.title}
          resourceName={'blog post'}
          onClose={closeModal}
          onConfirm={() => {
            publishBlogPostById(blog?.id, true)
              .then((response: any) => {
                if (response) {
                  closeModal();
                  showToast.success(response?.message ?? 'Post published successfully', );
                  queryClient.invalidateQueries({
                    queryKey: ['getBlogs']
                  });
                }
              })
              .catch((error: any) => {
                closeModal();
                showToast.error(error?.description ?? 'Could not publish the post. Please try again.', );
              });
          }}
        />
      )
    });
  };

  const handleUnpublishBlogPost = async (blog: Blog) => {
    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="unpublish"
          type='warning'
          resourceType={blog.title}
          modalName="Blog"
          resourceName={'blog post'}
          onClose={closeModal}
          onConfirm={() => {
            publishBlogPostById(blog?.id, false)
              .then((response: any) => {
                if (response) {
                  closeModal();
                  showToast.success(response?.message ?? 'Post unpublished successfully', );
                  queryClient.invalidateQueries({
                    queryKey: ['getBlogs']
                  });
                }
              })
              .catch((error: any) => {
                closeModal();
                showToast.error(error?.description ?? 'Could not unpublish the post. Please try again.', );
              });
          }}
        />
      )
    });
  };

  const handleBlogSetPostOfTheDay = async (blog: Blog) => {

    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="set to post of the day"
          modalName="Blog"
          type='success'
          resourceType={blog.title}
          resourceName={'blog post'}
          onClose={closeModal}
          onConfirm={async () => {
            await setPostOfTheDay(blog?.id)
              .then((response: any) => {
                if (response) {
                  closeModal();
                  showToast.success(response?.message ?? 'Post of the day set successfully', );
                  queryClient.invalidateQueries({
                    queryKey: ['getBlogs']
                  });
                }
              })
              .catch((error: any) => {
                closeModal();
                showToast.error(error?.description ?? 'Could not set the post of the day. Please try again.', );
              });
          }}
        />
      )
    });
  };

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div>
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div>
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'Source',
            accessor: 'sourceName',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Source Contact',
            accessor: 'sourceContact',
            Cell: (row: any) => {
              return <span>{row.cell.value}</span>;
            }
          },
          {
            Header: 'Title',
            accessor: 'title',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: '',
            accessor: 'tags',
            Cell: (row: any) => {
              return row.row.original.feature ? (
                <span className="border bg-green-500 dark:bg-green-600 text-white p-2 rounded-lg">
                  Post of the day
                </span>
              ) : (
                <span>{row.cell.value}</span>
              );
            }
          },
          {
            Header: 'Category',
            accessor: 'category',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Published',
            accessor: 'publish',
            Cell: (row: any) => {
              return <IconStatus status={row.cell.value} />;
            }
          },
          // {
          //   Header: 'Tags',
          //   accessor: 'tags',
          //   Cell: (row: any) => {
          //     return <span>{row.cell.value ? sentenceCase(row.cell.value) : '-'}</span>;
          //   }
          // },
          {
            Header: 'Writer',
            accessor: 'writer',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? `${row.cell.value.firstName} ${row.cell.value.lastName}`
                    : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Created Date',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return <span>{row.cell.value ? moment(row.cell.value).format('llll') : '-'}</span>;
            }
          },
          {
            Header: 'Updated Date',
            accessor: 'updatedOn',
            Cell: (row: any) => {
              return <span>{row.cell.value ? moment(row.cell.value).format('llll') : '-'}</span>;
            }
          },
          {
            Header: "Action",
            id: 'Action',
            Cell: (row: any) => {
              if (!getTableRowActions) return;
              return (
                <GTableActionColumn actions={getTableRowActions?.(row.row.original) ?? []} />
              );
            }
          }
        ]
      }
    ],
    []
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
    // getPolicies(page);
  };

  return (
    <Page title="blogs">
      <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <div className="">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">Blogs</h2>
            </div>

            <p className="text-sm text-slate-400 dark:text-darkBg-subText">
              View a list of all blogs and their details.
            </p>
          </div>

          <div className="w-fit ">
            <Link to={`${getDashboardBase()}/blogs/create-blog`}>
              <Button
                bgColor="primary"
                label={'Create Blog'}
                icon={<LiaReadme className="size-5 mr-1" />}
              />
            </Link>
          </div>
        </div>

        <div className="overflow-x-auto mt-4">
          <GTable
            selectionChanged={(selection: any) => {
            }}
            columns={screenWidth <= 550 ? columns : columns}
            data={blogs}
            classes="h-fit"
            isRowLinked={false}
            showSearch={false}
            onSearch={(searchString: string) => setSearchString(searchString)}
            searchPlaceholder={'Search blog...'}
            tableType={'blog'}
            paginationData={blogsPaginationData}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            loading={loading}
            columnClasses={'!text-[14px]'}
          />
        </div>
      </div>
      <ModalComponent />
    </Page>
  );
};

export default Blogs;
